import { useEffect, useRef, useState } from "react";
import type { Contact, Country, Currencies, InvoiceData } from "@/types";
import { useForm, useFieldArray, type FieldValues } from 'react-hook-form';
import { Eye, Plus, EyeOff, Trash, Banknote, ChevronDown, User } from 'lucide-react';
import { deletePreviewCfg, formatPrice, getPreviewCfg, savePreviewConfig, useClickOutside } from "@/utils";
import { currenciesMap } from "./currencies";
import { usePDF } from "@react-pdf/renderer";
import PdfDocument from "../pdf/Pdf";
import { Datepicker } from "../../shared/datepicker/Datepicker";
import { DateTime } from "luxon";
import { closestCenter, DndContext, DragEndEvent, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Draggable } from "./Draggable";
import { CustomMouseSensor } from "./sensors";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { customersRepository } from "@/database/repositories/customersRepository";
import { CustomerWithContacts, InsertInvoice } from "@/database/schema";
import { invoicesRepository } from "@/database/repositories/invoicesRepository";
import { $customers, $invoices } from "@/stores";
import { CSSTransition } from "react-transition-group";
import { Flip, toast, ToastContainer } from "react-toastify";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { countries } from "@/shared/countriesDropdown/countries";
import { CountriesDropdown } from "@/shared/countriesDropdown/CountiesDropdown";
import { contactsRepository } from "@/database/repositories/contactsRepository";
import "./InvoiceArea.scss";
import { useDb } from "@/database/provider/useDb";
import * as Sentry from "@sentry/react";

const InvoiceArea = () => {
    const db = useDb();
    const navigate = useNavigate();
    const { state } = useLocation();
    const [searchParams] = useSearchParams();
    const [dateIssued, setDateIssued] = useState(DateTime.now());
    const [dateDue, setDateDue] = useState(DateTime.now().plus({ weeks: 1 }));
    const [customers, setCustomers] = useState<CustomerWithContacts[]>([]);
    const [customersFilter, setFilterCustomers] = useState<CustomerWithContacts[]>([]);
    const currencies = Array.from(currenciesMap.values());
    const [isEdit, setIsEdit] = useState(false);
    const [showCustomersDropdown, setShowCustomersDropdown] = useState(false);
    const companiesListRef = useRef(null);
    const contactsListRef = useRef(null);
    const [disabledPreviewButton, setDisablePreviewButton] = useState(false);
    const [customerCountries, setCustomerCountries] = useState<Country[]>(countries);
    const [isCustomerCountriesOpen, setIsCustomerCountriesOpen] = useState(false);
    const [userCountries, setUserCountries] = useState<Country[]>(countries);
    const [isUserCountriesOpen, setIsUserCountriesOpen] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState<CustomerWithContacts | null>(null);
    const [addContactOpen, setAddContactOpen] = useState(false);
    const [selectedContact, setSelectedContact] = useState<Contact | null>(null)
    const [isContactListOpen, setIsContactListOpen] = useState(false);
    const {
        register, handleSubmit, control, getValues, setFocus, setValue, watch, reset, trigger, formState: { errors, isSubmitted }
    } = useForm<InvoiceData>({
        defaultValues: {
            currency: "USD",
            invoiceNumber: "",
            dateIssued: dateIssued.toFormat('dd/MM/yyyy'),
            dateDue: dateDue.plus({ weeks: 1 }).toFormat('dd/MM/yyyy'),
            invoiceName: "",
            billFrom: "",
            billFromAddress: null,
            billFromCountry: "",
            billFromEmail: "",
            billFromPhone: null,
            billTo: "",
            billToAddress: null,
            billToCountryCode: null,
            billToContact: {
                email: "",
                phone: null,
                firstName: "",
                lastName: ""
            },
            customerId: null,
            contactId: null,
            tasks: [{ taskName: "", taskDescription: "", taskCost: 1, taskQuantity: 1 }],
            paymentTerms: "",
            clientNotes: "",
        },
    });

    const { fields: tasks, append, remove } = useFieldArray({
        control,
        name: "tasks",
    });

    const [showPaymentTerms, setShowPaymentTerms] = useState(false);
    const [showClientNotes, setShowClientNotes] = useState(false);
    const [showPaymentStub, setShowPaymentStub] = useState(false);
    const mouseSensor = useSensor(CustomMouseSensor);
    const sensors = useSensors(mouseSensor);

    const addTask = async () => {
        append({
            taskName: "",
            taskDescription: "",
            taskCost: 1,
            taskQuantity: 1,
        });
    };

    const selectedCurrency = watch("currency");
    const watchTasks = watch("tasks");
    const formData = watch();

    const totalCost = watchTasks.reduce((total, task) => {
        return total + (task.taskQuantity * task.taskCost);
    }, 0);

    function mapFormDataToInvoiceData(data: FieldValues): InvoiceData {
        const result = {
            ...data,
            tasks: data.tasks,
            totalToPay: totalCost,
            paymentTerms: showPaymentTerms ? data.paymentTerms : null,
            clientNotes: showClientNotes ? data.clientNotes : null,
            invoiceStub: showPaymentStub
        }
        return result as InvoiceData;
    }

    const invoiceData: InvoiceData = mapFormDataToInvoiceData(getValues());

    usePDF({ document: <PdfDocument data={invoiceData} /> });

    const updateDateIssued = (date: DateTime) => {
        setDateIssued(date);
        setValue("dateIssued", date.toFormat('dd/MM/yyyy'));
        if (date > dateDue) {
            const nextWeek = date.plus({ weeks: 1 })
            setDateDue(nextWeek);
            setValue("dateDue", nextWeek.toFormat('dd/MM/yyyy'));
        }
    }
    const updateDateDue = (date: DateTime) => {
        if (date < dateIssued) setDateDue(dateIssued.plus({ weeks: 1 }))
        else setDateDue(date);
        setValue("dateDue", date.toFormat('dd/MM/yyyy'));
    }

    const saveInvoice = async () => {
        try {
            let { customerId, contactId } = invoiceData;
            const { billToContact: { email } } = invoiceData;
            if (!customerId) {
                customerId = await createNewCustomer();
                invoiceData.customerId = customerId;
            };

            const contacts = await contactsRepository.getCustomerContacts(db, customerId);
            if (!contacts.some((item) => item.email === email)) {
                contactId = await createNewContact(customerId);
            }

            const invoice: InsertInvoice = {
                ...invoiceData,
                invoiceData: invoiceData,
                customerId,
                contactId: contactId as number,
                status: "unpaid",
                publicToken: null,
                privateToken: null,
                lastModified: new Date()
            };

            const res = await invoicesRepository.add(db, invoice);
            $invoices.set({ isLoad: true, values: await invoicesRepository.getAllUnArchived(db) });
            return res;
        }
        catch (err) {
            Sentry.captureException(err);
        }
    }

    const updateInvoice = async () => {
        try {
            let { customerId, contactId } = invoiceData;
            const { billToContact: { email } } = invoiceData
            if (!customerId) customerId = await createNewCustomer();
            const contacts = await contactsRepository.getCustomerContacts(db, customerId);

            if (!contacts.some((item) => item.email === email)) {
                contactId = await createNewContact(customerId);
            }

            const invoice: InsertInvoice = {
                ...invoiceData,
                invoiceData: invoiceData,
                customerId,
                contactId: contactId as number,
                lastModified: new Date()
            };

            const res = await invoicesRepository.update(db, invoice);
            $invoices.set({ isLoad: true, values: await invoicesRepository.getAllUnArchived(db) });
            return res;
        }
        catch (err) {
            Sentry.captureException(err);
        }
    }

    const createNewCustomer = async () => {
        const { billTo: name,
            billToAddress: address,
            billToCountry: country,
            billToCountryCode
        } = invoiceData;

        const customer = await customersRepository.add(db, {
            name,
            address,
            country: {
                name: country as string,
                code: billToCountryCode as string
            },
        });
        $customers.value.values.push({ ...customer, activeContacts: [], archivedContacts: [] });
        return customer.id;
    }

    const createNewContact = async (customerId: number) => {
        const { billToContact } = invoiceData;
        const res = await contactsRepository.addContact(db, { ...billToContact, customerId, id: undefined });
        const storeCustomer = $customers.value.values.find((item) => item.id === customerId) as CustomerWithContacts;
        if (storeCustomer) storeCustomer.activeContacts.push(res);
        return res.id;
    }

    const selectCustomer = (customer: CustomerWithContacts) => {
        setValue("billToAddress", customer.address);
        setValue("billTo", customer.name, { shouldValidate: true });
        setValue("billToCountry", customer.country?.name as string, { shouldValidate: true });
        setValue("billToCountryCode", customer.country?.code as string)
        setValue("customerId", customer.id);
        setSelectedCustomer(customer);
    }

    const handleCustomerSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setValue("customerId", null);
        const query = event.target.value.toLowerCase();
        const filtered = customers.filter((customer) => customer.name.toLowerCase().includes(query));
        setFilterCustomers(filtered);
        const match = customers.find((customer) => customer.name.toLowerCase() === query);
        if (match) {
            setValue("billTo", filtered[0].name, { shouldValidate: true });
            setSelectedCustomer(filtered[0]);
            setShowCustomersDropdown(false);
            setAddContactOpen(false);
            return;
        }
        setValue("billToContact", {
            email: "",
            firstName: "",
            lastName: "",
            phone: ""
        })
        setValue("billToAddress", "");
        setValue("billToCountry", "");
        setAddContactOpen(true);
        setSelectedCustomer(null);
        setShowCustomersDropdown(true);
        setValue("billTo", event.target.value, { shouldValidate: true });
    };

    const handleCountrySearch = (event: React.ChangeEvent<HTMLInputElement>,
        set: React.Dispatch<React.SetStateAction<Country[]>>, type: "billTo" | "billFrom") => {
        const { value } = event.target;
        const filtered = countries.filter((country) => country.name.toLowerCase().includes(value.toLowerCase()));
        if (filtered.length === 1
            && filtered[0].name.toLowerCase() === value.toLowerCase()) {
            if (type === "billTo") {
                setValue("billToCountry", filtered[0].name);
                setIsCustomerCountriesOpen(false);
            };
            if (type === "billFrom") {
                setValue("billFromCountry", filtered[0].name);
                setIsUserCountriesOpen(false);
            }
        }
        set(filtered);
    }

    const handleSubmitForm = async () => {
        setDisablePreviewButton(true);
        savePreviewConfig(invoiceData);
        try {
            const existsNumber = await invoicesRepository.getById(db, formData.invoiceNumber);

            const edit = searchParams.get("edit") === "true";
            if (edit) await updateInvoice();
            else {
                if (existsNumber) {
                    toast.error('This invoice already exists!', {
                        position: "top-right",
                        autoClose: 5000,
                        hideProgressBar: true,
                        closeOnClick: false,
                        pauseOnHover: true,
                        theme: "light",
                        transition: Flip,
                    });
                    return setFocus("invoiceNumber");
                }
                await saveInvoice();
            }

            deletePreviewCfg();

            navigate({
                pathname: "/preview",
                search: `template=${invoiceData.invoiceNumber}`
            });
        }
        catch (err: any) {
            toast.error("Something went wrong!", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
            throw new Error(err.message)
        }
        finally {
            setDisablePreviewButton(false);
        }
    }

    useClickOutside(companiesListRef, () => {
        setShowCustomersDropdown(false);
    }, "invoice-area__company-name");

    useClickOutside(contactsListRef, () => {
        setIsContactListOpen(false);
    }, "invoice-area__select-contact");

    useEffect(() => {
        try {
            (async () => {
                const edit = searchParams.get("edit") === "true";
                const template = searchParams.get("template");
                setIsEdit(edit);

                const invoices = (await invoicesRepository.getAll(db));

                let maxNumericNumber = 0;
                if (invoices.length > 0) {
                    maxNumericNumber = +invoices
                        .filter(obj => !isNaN(Number(obj.invoiceNumber)))
                        .reduce((max, obj) =>
                            Number(obj.invoiceNumber) > Number(max.invoiceNumber) ? obj : max,
                            invoices[0]
                        ).invoiceNumber;
                }

                let invoiceNumber = `${maxNumericNumber + 1}`;

                if (!$customers.value.isLoad) {
                    const customers = await customersRepository.getAllWithContacts(db);
                    setCustomers(customers);
                    $customers.set({ isLoad: true, values: customers });
                }
                else setCustomers($customers.value.values);

                if (!edit) setValue("invoiceNumber", invoiceNumber);

                if (template) {
                    const data = await invoicesRepository.getById(db, template);
                    const { invoiceData: invoice, customerId, billToContact } = data;
                    const nextInvoiceNumber = +data.invoiceNumber + 1;
                    const existsInvoice = await invoicesRepository.getById(db, nextInvoiceNumber.toString());
                    if (!isNaN(nextInvoiceNumber) && !existsInvoice) invoiceNumber = nextInvoiceNumber.toString();

                    if (billToContact) {
                        const contact = await contactsRepository.getById(db, billToContact.id as number);
                        if (contact) {
                            setValue("contactId", contact.id);
                            setSelectedContact(contact);
                        }

                    }

                    if (customerId) {
                        const customer = await customersRepository.getById(db, customerId);
                        const contacts = await contactsRepository.getCustomerContacts(db, customerId);
                        const customerWithContacts = { ...customer, contacts };
                        setSelectedCustomer(customerWithContacts);
                        selectCustomer(customerWithContacts);
                    }
                    if (edit) {
                        reset({ ...invoice });
                        savePreviewConfig(invoice);
                    }
                    else reset({
                        ...invoice, invoiceNumber,
                        billToAddress: getValues("billToAddress"),
                        contactId: getValues("contactId"),
                        dateIssued: getValues("dateIssued")
                    });
                    if (invoice.clientNotes) setShowClientNotes(true);
                    if (invoice.paymentTerms) setShowPaymentTerms(true);
                    if (invoice.invoiceStub) setShowPaymentStub(true);
                }
                else {
                    const cached = getPreviewCfg();
                    if (cached) {
                        reset(cached);
                        if (cached.clientNotes) setShowClientNotes(true);
                        if (cached.paymentTerms) setShowPaymentTerms(true);
                        if (cached.invoiceStub) setShowPaymentStub(true);
                        const { customerId, contactId } = cached;

                        if (customerId) {
                            const customer = await customersRepository.getById(db, customerId);
                            setSelectedCustomer(customer);
                        }
                        if (contactId) {
                            const contact = await contactsRepository.getById(db, contactId);
                            setSelectedContact(contact);
                        }
                    }
                }
            })();
        }
        catch (err: any) {
            throw new Error(err.message)
        }
    }, []);

    useEffect(() => {
        const handler = setTimeout(() => {
            savePreviewConfig(formData);
        }, 500);
        return () => clearTimeout(handler);
    }, [formData]);

    return (
        <>
            <ToastContainer />
            <form className="generate" onSubmit={handleSubmit(handleSubmitForm)}>
                <div className="controls">
                    {(state && state.prevPath) && <Link className="preview-controls__back" type="button" to={`${state.prevPath}`}>
                        Back
                    </Link>}
                    <button
                        className="controls__preview"
                        type="submit"
                        disabled={Object.keys(errors).length > 0 || disabledPreviewButton} >
                        Preview invoice
                    </button>
                </div>
                <div className="invoice-area" >
                    <div className="invoice-area__head">
                        <div className="invoice-area__invoice-number">
                            <span className="invoice-area__label">Invoice #</span>
                            {!isEdit ? <input
                                className={`invoice-area__number-inout ${errors.invoiceNumber ? "input-error" : ""} invoice-input`}
                                {...register("invoiceNumber", {
                                    required: true, pattern: {
                                        value: /^[a-zA-Z0-9-]+$/,
                                        message: "Incorrect format",
                                    },
                                })}
                                autoComplete="off" /> :
                                <span> {getValues("invoiceNumber")}</span>}
                        </div>
                        <div className="invoice-area__datepickers">
                            <div className="invoice-area__datepicker-container">
                                <Datepicker
                                    type="dateIssuedSelector"
                                    name="Date issued:"
                                    dateIssued={dateIssued}
                                    dateDue={dateDue}
                                    isError={errors.dateIssued ? true : false}
                                    setValue={updateDateIssued} />
                            </div>

                            <div className="invoice-area__datepicker-container">
                                <Datepicker
                                    type="dateDueSelector"
                                    name="Date due:"
                                    dateIssued={dateIssued}
                                    dateDue={dateDue}
                                    isError={errors.dateIssued ? true : false}
                                    setValue={updateDateDue} />
                            </div>

                            <input className="date-input" type="date"  {...register("dateIssued")} />
                            <input className="date-input" type="date"  {...register("dateDue")} />
                        </div>
                    </div>
                    <div className="invoice-area__form">
                        <div className="invoice-area__title">
                            <h2 className="invoice-area__title-text">Invoice</h2>
                            <input
                                className={`invoice-area__job-description invoice-input ${errors.invoiceName ? "input-error" : ""}`}
                                placeholder="Job description"
                                autoComplete="off"
                                {...register("invoiceName", { required: true })}
                            />
                        </div>
                        <hr />
                        <div className="invoice-area__main">
                            <div className="invoice-area__bills">
                                <div className="invoice-area__bill-info">
                                    <div className="invoice-area__customers-select">
                                        <span className="invoice-area__label">Bill From:</span>
                                    </div>

                                    <div className="invoice-area__inputs">
                                        <input
                                            className={`invoice-area__company-name invoice-input ${errors.billFrom ? "input-error" : ""}`}
                                            placeholder="Your Company Name"
                                            type="text"
                                            {...register("billFrom", { required: true })}
                                            autoComplete="off"
                                        />
                                        <textarea
                                            className="invoice-area__company-adders"
                                            placeholder="Company Address"
                                            {...register("billFromAddress")}
                                        ></textarea>
                                        <div className="invoice-area__customer-container">
                                            <input
                                                className="invoice-area__country invoice-input"
                                                placeholder="Country"
                                                onClick={() => {
                                                    setUserCountries(countries);
                                                    setIsUserCountriesOpen(true);
                                                    if (isCustomerCountriesOpen) setIsCustomerCountriesOpen(false);
                                                }}
                                                {...register("billFromCountry")}
                                                onChange={(e) => {
                                                    setIsUserCountriesOpen(true);
                                                    handleCountrySearch(e, setUserCountries, "billFrom");
                                                }}
                                                autoComplete="off"
                                            />
                                            <div className="invoice-area__countries-container">
                                                <CSSTransition in={isUserCountriesOpen}
                                                    timeout={200}
                                                    classNames="fade"
                                                    exit={false}
                                                    unmountOnExit>
                                                    <CountriesDropdown
                                                        countries={userCountries}
                                                        onSelect={(country: Country) => {
                                                            setValue("billFromCountry", country.name, { shouldValidate: true });
                                                            setIsUserCountriesOpen(false);
                                                        }}
                                                        close={() => {
                                                            if (userCountries.length === 0) setValue("billFromCountry", null);
                                                            setIsUserCountriesOpen(false);
                                                        }}
                                                    />
                                                </CSSTransition>
                                            </div>

                                        </div>

                                        <input
                                            className="invoice-area__email invoice-input"
                                            placeholder="Email"
                                            type="email"
                                            {...register("billFromEmail")}
                                        />
                                        <input
                                            className="invoice-area__phone invoice-input"
                                            placeholder="Phone Number"
                                            type="tel"
                                            {...register("billFromPhone")}
                                        />
                                    </div>
                                </div>
                                <div className="invoice-area__bill-info">
                                    <div className="invoice-area__customers-select">
                                        <span className="invoice-area__label">Bill To:</span>
                                    </div>
                                    <div className="invoice-area__inputs">
                                        <div className="invoice-area__customer-container">
                                            <input
                                                className={`invoice-area__company-name invoice-input ${errors.billTo ? "input-error" : ""}`}
                                                placeholder="Customer"
                                                {...register("billTo", { required: true })}
                                                onFocus={() => {
                                                    setFilterCustomers(customers);
                                                    setShowCustomersDropdown(true);
                                                    setAddContactOpen(false);
                                                }}
                                                onChange={handleCustomerSearch}
                                                autoComplete="off"
                                                disabled={isEdit}
                                            />
                                            <CSSTransition in={showCustomersDropdown && customersFilter.length > 0}
                                                timeout={200}
                                                classNames="fade"
                                                exit={false}
                                                unmountOnExit>
                                                <div className="invoice-area__customers" ref={companiesListRef}>
                                                    <ul className="invoice-area__customers-list">
                                                        {customersFilter.map((customer, i) =>
                                                            <li className="invoice-area__customer" key={i}>
                                                                <button className="invoice-area__customer-select-button" type="button" onClick={() => {
                                                                    selectCustomer(customer);
                                                                    setShowCustomersDropdown(false);
                                                                    setSelectedContact(null);
                                                                    setAddContactOpen(false);
                                                                }}>
                                                                    {customer.name}
                                                                    <span className="invoice-area__contacts-count">
                                                                        <User width={14} height={14} />
                                                                        {customer.activeContacts.length}
                                                                    </span>
                                                                </button>
                                                            </li>)}
                                                    </ul>
                                                </div>
                                            </CSSTransition>
                                        </div>
                                        <textarea
                                            className="invoice-area__company-adders"
                                            placeholder="Company Address"
                                            {...register("billToAddress")}
                                            disabled={selectedCustomer !== null}
                                        ></textarea>
                                        <div className="invoice-area__customer-container">
                                            <input
                                                className={`invoice-area__country invoice-input ${errors.billToCountry ? "input-error" : ""}`}
                                                placeholder="Country"
                                                {...register("billToCountry", { required: true })}
                                                onClick={() => {
                                                    setCustomerCountries(countries);
                                                    if (!selectedCustomer) setIsCustomerCountriesOpen(true);
                                                    if (isUserCountriesOpen) setIsUserCountriesOpen(false);
                                                }}
                                                onChange={(e) => {
                                                    setIsCustomerCountriesOpen(true)
                                                    handleCountrySearch(e, setCustomerCountries, "billTo");
                                                }}
                                                autoComplete="off"
                                                disabled={selectedCustomer !== null}
                                            />
                                            <div className="invoice-area__countries-container">
                                                <CSSTransition in={isCustomerCountriesOpen}
                                                    timeout={200}
                                                    classNames="fade"
                                                    exit={false}
                                                    unmountOnExit>
                                                    <CountriesDropdown
                                                        countries={customerCountries}
                                                        onSelect={({ name, code }: Country) => {
                                                            setValue("billToCountry", name, { shouldValidate: true });
                                                            setValue("billToCountryCode", code, { shouldValidate: true });
                                                            setIsCustomerCountriesOpen(false);
                                                        }}
                                                        close={() => {
                                                            setValue("billToCountry", null);
                                                            setIsCustomerCountriesOpen(false);
                                                        }}
                                                    />
                                                </CSSTransition>
                                            </div>
                                        </div>
                                        <label className="invoice-area__label invoice-area__contact-label">Contact:</label>
                                        {(!selectedCustomer || selectedCustomer.activeContacts.length === 0 || addContactOpen) ? <div>
                                            <div className="invoice-area__names">
                                                <input
                                                    className="invoice-area__first-name invoice-input"
                                                    placeholder="First name"
                                                    {...register("billToContact.firstName")}
                                                />
                                                <input
                                                    className="invoice-area__last-name invoice-input"
                                                    placeholder="Last name"
                                                    {...register("billToContact.lastName")}
                                                />
                                            </div>
                                            <input
                                                className={`invoice-area__contact-input invoice-input ${errors.billToContact ? "input-error" : ""}`}
                                                placeholder="Email"
                                                type="email"
                                                {...register("billToContact.email", { required: true })}
                                            />
                                            <input
                                                className="invoice-area__contact-input invoice-input"
                                                placeholder="Phone Number"
                                                type="tel"
                                                {...register("billToContact.phone")}
                                            />
                                            {selectedCustomer && selectedCustomer.activeContacts.length > 0 && <button className="invoice-area__cancel-contact" type="button" onClick={() => {
                                                setAddContactOpen(false);
                                                if (selectedContact) {
                                                    setValue("billToContact.email", selectedContact.email);
                                                }
                                            }}>
                                                Cancel
                                            </button>}
                                        </div> :
                                            <div className="invoice-area__select-contact-container">
                                                <div
                                                    className="invoice-area__selected-contact"
                                                    onClick={() => {
                                                        setIsContactListOpen(!isContactListOpen);
                                                    }}>
                                                    <button className={`invoice-area__select-contact ${errors.billToContact ? "input-error" : ""}`}
                                                        type="button">
                                                        {selectedContact ? <div className="invoice-area__selected-contact">
                                                            <p>{selectedContact.email}</p>
                                                        </div> : <p>
                                                            Select contact
                                                        </p>}
                                                        <ChevronDown width={13} height={13} />
                                                    </button>
                                                </div>
                                                <CSSTransition in={isContactListOpen}
                                                    timeout={200}
                                                    classNames="fade"
                                                    exit={false}
                                                    unmountOnExit>
                                                    <div className="invoice-area__contacts-container" ref={contactsListRef}>
                                                        <ul className="invoice-area__contacts-list">
                                                            {selectedCustomer.activeContacts.map((contact, i) => <li className="invoice-area__contact" key={i}>
                                                                <button type="button" onClick={() => {
                                                                    setSelectedContact(contact);
                                                                    setIsContactListOpen(false);
                                                                    setValue("billToContact.email", contact.email, { shouldValidate: true });
                                                                    setValue("contactId", contact.id);
                                                                }}>
                                                                    {contact.firstName}{contact.lastName} {contact.email}
                                                                </button>
                                                            </li>)}
                                                        </ul>
                                                    </div>
                                                </CSSTransition>

                                                <button className="invoice-area__add-contact" type="button" onClick={() => {
                                                    setAddContactOpen(true);
                                                    setValue("billToContact.email", "");
                                                    setValue("billToContact.firstName", "");
                                                    setValue("billToContact.lastName", "");
                                                    setValue("billToContact.phone", "");
                                                }}><Plus width={12} height={12} />
                                                    <p>Add Contact</p>
                                                </button>
                                            </div>}
                                    </div>
                                </div>
                            </div>
                            <div className="invoice-area__currency">
                                <span className="invoice-area__label invoice-area__currency-label">
                                    <Banknote width={18} height={18} />
                                    Currency:</span>
                                <select
                                    className="form-select"
                                    {...register("currency")}
                                    value={selectedCurrency}
                                    onChange={(e) => setValue("currency", e.target.value as Currencies)}
                                >
                                    {currencies.map((currency) => (
                                        <option key={currency.code} value={currency.code}>
                                            {currency.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="invoice-area__tasks">
                                <div className="invoice-area__info">
                                    <span className="invoice-area__description">Tasks</span>
                                    <span className="invoice-area__cost">Cost</span>
                                    <span className="invoice-area__quantity">QTY</span>
                                    <span className="invoice-area__price">Price</span>
                                </div>
                                <DndContext collisionDetection={closestCenter} sensors={sensors} modifiers={[restrictToVerticalAxis]} onDragEnd={(e: DragEndEvent) => {
                                    const { active, over } = e;
                                    if (over && over.id !== active.id) {
                                        const updatedTasks = [...watchTasks];
                                        [updatedTasks[active.id as number], updatedTasks[over.id as number]]
                                            = [updatedTasks[over.id as number], updatedTasks[active.id as number]];
                                        reset({ ...getValues(), tasks: updatedTasks }, { keepIsSubmitted: true });
                                        setTimeout(() => {
                                            if (isSubmitted) trigger("tasks");
                                        }, 0);
                                    }
                                }}>
                                    <SortableContext items={tasks.map((_, index) => index)} strategy={verticalListSortingStrategy} disabled={tasks.length === 1}>
                                        <div className="invoice-area__tasks-container">
                                            {tasks.map(({ id }, i) =>
                                                <Draggable key={id} index={i}>
                                                    <div className={`invoice-area__task ${tasks.length > 1 ? "draggable" : ""}`} key={id}>
                                                        <button className="invoice-area__remove-task" type="button" disabled={tasks.length === 1}
                                                            onClick={() => {
                                                                if (tasks.length !== 1) remove(i)
                                                            }}
                                                            data-no-dnd="true">
                                                            <span data-no-dnd="true">
                                                                <Trash width={14} height={14} />
                                                            </span>
                                                        </button>
                                                        <div
                                                            className="invoice-area__task-description invoice-area__description"
                                                        >
                                                            <input
                                                                placeholder="Task name"
                                                                className={`invoice-area__task-name invoice-input ${errors.tasks?.[i]?.taskName ? "input-error" : ""}`}
                                                                {...register(`tasks.${i}.taskName`, { required: true })}
                                                                data-no-dnd="true"
                                                            />
                                                            <textarea
                                                                className="invoice-area__task-description"
                                                                placeholder="Task Description"
                                                                {...register(`tasks.${i}.taskDescription`)}
                                                                data-no-dnd="true"
                                                            ></textarea>
                                                        </div>
                                                        <div className="invoice-area__task-cost invoice-area__cost">
                                                            <input
                                                                className={`invoice-input ${errors.tasks?.[i]?.taskCost ? "input-error" : ""}`}
                                                                type="number" step="0.01"
                                                                {...register(`tasks.${i}.taskCost`, { valueAsNumber: true, required: true, min: 0.01 })}
                                                                data-no-dnd="true"
                                                            />
                                                        </div>
                                                        <div
                                                            className="invoice-area__task-quantity invoice-area__quantity"
                                                        >
                                                            <input
                                                                className={`invoice-input ${errors.tasks?.[i]?.taskQuantity ? "input-error" : ""}`}
                                                                type="number" step="1"
                                                                {...register(`tasks.${i}.taskQuantity`,
                                                                    { valueAsNumber: true, required: true, min: 1 })}
                                                                onChange={(e) => {
                                                                    let value = e.target.valueAsNumber;
                                                                    if (e.target.value.length > 0 && (isNaN(value) || value < 1)) {
                                                                        value = 1;
                                                                    }
                                                                    setValue(`tasks.${i}.taskQuantity`, value, { shouldValidate: true });
                                                                }}
                                                                data-no-dnd="true"
                                                            />
                                                        </div>
                                                        <div className="invoice-area__task-price invoice-area__price">
                                                            <p className="invoice-area__price-text">
                                                                {currenciesMap.get(selectedCurrency)?.symbol}
                                                                {formatPrice((watchTasks[i].taskQuantity) * (watchTasks[i].taskCost))}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </Draggable>)}
                                            <button className="invoice-area__add-task" type="button" onClick={addTask}>
                                                <Plus width={12} height={12} />Add Line</button>
                                        </div>
                                    </SortableContext>
                                </DndContext>
                            </div>
                        </div>
                        <hr />
                        <div className="invoice-area__summary">
                            <div className="invoice-area__terms-notes">
                                <div className="invoice-area__note">
                                    <div className="invoice-area__control">
                                        <span className="invoice-area__label">Payment Terms</span>
                                        <button className="invoice-area__show-button" type="button" onClick={() => setShowPaymentTerms(!showPaymentTerms)}>
                                            {showPaymentTerms ? <Eye width={14} height={14} /> : <EyeOff width={14} height={14} />}
                                            <p className="invoice-area__show-button-text">Show</p>
                                        </button>
                                    </div>
                                    {showPaymentTerms && <textarea
                                        className="invoice-area__note-input"
                                        {...register(`paymentTerms`)}></textarea>}
                                </div>
                                <div className="invoice-area__note">
                                    <div className="invoice-area__control">
                                        <span className="invoice-area__label">Client Notes</span>
                                        <button className="invoice-area__show-button" type="button" onClick={() => setShowClientNotes(!showClientNotes)}>
                                            {showClientNotes ? <Eye width={14} height={14} /> : <EyeOff width={14} height={14} />}
                                            <p className="invoice-area__show-button-text">Show</p>
                                        </button>
                                    </div>
                                    {showClientNotes &&
                                        <textarea className="invoice-area__note-input" {...register("clientNotes")}></textarea>}
                                </div>
                            </div>
                            <div className="invoice-area__total-to-pay">
                                <div className="invoice-area__value">
                                    <p>Invoice total</p>
                                    <p>{formatPrice(totalCost)}{currenciesMap.get(selectedCurrency)?.symbol}</p>
                                </div>
                                <div className="invoice-area__value">
                                    <p>Amount due ({currenciesMap.get(selectedCurrency)?.code})</p>
                                    <p>{formatPrice(totalCost)}{currenciesMap.get(selectedCurrency)?.symbol}</p>
                                </div>
                            </div>
                        </div>
                        <hr className="hr-puncture" />
                        <div className="invoice-area__stub">
                            <div className="invoice-area__control">
                                <span className="invoice-area__label">Payment Stub</span>
                                <button className="invoice-area__show-button" type="button" onClick={() => setShowPaymentStub(!showPaymentStub)}>
                                    {showPaymentStub ? <Eye width={14} height={14} /> : <EyeOff width={14} height={14} />}
                                    Show Payment Stub
                                </button>
                            </div>
                            {showPaymentStub && <div className="invoice-area__stub-container">
                                <div className="invoice-area__from-details">
                                    <p>{watch("billFrom")}</p>
                                    <p>Tel: {watch("billFromPhone")}</p>
                                    <p>Email: {watch("billFromEmail")}</p>
                                </div>
                                <div className="invoice-area__invoice-details">
                                    <div className="invoice-area__details-string">
                                        <p>Client</p>
                                        <p>{watch("billTo")}</p>
                                    </div>
                                    <div className="invoice-area__details-string">
                                        <p>Invoice #</p>
                                        <p>{watch("invoiceNumber")}</p>
                                    </div>
                                    <hr />
                                    <div className="invoice-area__details-string">
                                        <p>Amount Due ({currenciesMap.get(selectedCurrency)?.code})</p>
                                        <p>{formatPrice(totalCost)}{currenciesMap.get(selectedCurrency)?.symbol}</p>
                                    </div>
                                </div>
                            </div>}
                        </div>
                    </div>
                </div>
            </form >
        </>);
}
export default InvoiceArea